// Was eine Mappe im Risiken-Reiter über ihr Objekt liest — Typen und
// Lesehelfer, gemeinsam für die Objektmappen (Betrieb) und die
// Immobilienmappe. Beide fragen dieselbe Route (`risk-objects/by-contacts`);
// ohne diese Stelle stünden Typ und Merkmalszugriff zweimal im Code und
// drifteten beim nächsten neuen Feld auseinander.

import { apiUrl } from "../../utils/api";

export interface MappeContact {
  id: number;
  lastname?: string | null;
  firstName?: string | null;
  displayName?: string | null;
}

export interface MappeRisk {
  id: number;
  riskType?: string | null;
  name?: string | null;
  versicherungssumme?: number | string | null;
  contactID?: number | null;
  contractID?: number | null;
  contract?: {
    id: number;
    contractNbr?: string | null;
    branch?: string | null;
  } | null;
  contacts?: MappeContact[] | null;
  directContact?: MappeContact | null;
  characteristics?:
    | { nameBezeichnung?: string | null; riskCharacteristicValue?: string | null }[]
    | null;
}

/** Offene Vorgänge/Schäden je Risikoobjekt (`risk-objects/open-counts`). */
export type OpenCounts = Record<number, { openVorgaenge: number; openSchaeden: number }>;

export const eur = (v: number) => `${Math.round(v).toLocaleString("de-DE")} €`;

/**
 * Das Backend liefert die signierte Bild-URL als `/api/documents/…` — also
 * origin-relativ. Als `<img src>` würde das bei absolut konfigurierter
 * VITE_API_BASE (lokale Entwicklung: http://localhost:3500/api) auf den
 * Vite-Dev-Server zeigen statt auf die API. Deshalb gegen die konfigurierte
 * Basis auflösen; die Query (exp/sig/tenantId) bleibt dabei erhalten.
 */
export const resolveSignedUrl = (url: string): string => apiUrl(url.replace(/^\/?api\//, ""));

/** Erstes gefülltes Merkmal aus einer Reihe von Schlüsseln (ohne Groß-/Kleinschreibung). */
export const charOf = (risk: MappeRisk, ...keys: string[]): string | null => {
  for (const key of keys) {
    const hit = risk.characteristics?.find(
      (c) => (c.nameBezeichnung ?? "").trim().toLowerCase() === key.toLowerCase(),
    );
    const val = hit?.riskCharacteristicValue?.trim();
    if (val) return val;
  }
  return null;
};

export const contactLabel = (c?: MappeContact | null): string => {
  if (!c) return "—";
  if (c.displayName) return c.displayName;
  return [c.firstName, c.lastname].filter(Boolean).join(" ") || `Kontakt ${c.id}`;
};

/** Der Kontakt, dem das Objekt gehört — dieselbe Reihenfolge in beiden Mappen. */
export const eigentuemerVon = (risk: MappeRisk): MappeContact | undefined => {
  const ownerId = risk.contactID ?? risk.contacts?.[0]?.id ?? risk.directContact?.id ?? null;
  return (
    risk.contacts?.find((c) => c.id === ownerId) ??
    risk.directContact ??
    risk.contacts?.[0] ??
    undefined
  );
};

/**
 * Ist das Objekt ein Gebäude? Dieselbe Prüfung wie die Blockzuordnung der
 * Objektmappen — Photovoltaik zuerst, weil eine Dachanlage oft als
 * Immobilien-Risiko erfasst ist und dann kein Gebäude meint.
 */
export const istGebaeude = (risk: MappeRisk): boolean => {
  const t = `${risk.riskType ?? ""} ${charOf(risk, "Vermögensart") ?? ""}`.toLowerCase();
  if (t.includes("photovolt") || t.includes("pv-anlage") || t.includes("solar")) return false;
  if (t.includes("kfz") || t.includes("fahrzeug")) return false;
  return (
    t.includes("immo") ||
    t.includes("gebäude") ||
    t.includes("gebaeude") ||
    t.includes("grundstück")
  );
};

/**
 * Eine Beteiligtenliste des Immobilien-Fragebogens (`mieter`, `paechter`,
 * `sonstigeNutzer`). Sie liegt als JSON-String im Merkmal, weil der
 * Übernahmepfad jedes Antwortobjekt so ablegt (`public-form.service`,
 * `makeChars`). Unlesbares JSON ergibt eine leere Liste statt eines Fehlers —
 * eine kaputte Antwort darf die Mappe nicht leeren.
 */
export const beteiligtenNamen = (roh: string | null): string[] => {
  if (!roh) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(roh);
  } catch {
    // Altbestand: einfacher Freitext statt Liste.
    return roh.trim() ? [roh.trim()] : [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((e) => {
      if (typeof e === "string") return e.trim();
      if (e && typeof e === "object") {
        const o = e as Record<string, unknown>;
        const ref = o.kontaktRef as Record<string, unknown> | undefined;
        return String(o.name ?? ref?.label ?? "").trim();
      }
      return "";
    })
    .filter(Boolean);
};
